/**
 * BD Geo Detection - Production-ready geo-based country detection and redirection
 * 
 * Features:
 * - Automatic country detection via Shopify Liquid (request.country_code) or JS fallback
 * - Zalando-style country grid UI
 * - Multi-language support (Italian, French, English)
 * - User confirmation before redirect
 * - localStorage persistence
 * - Graceful fallback if detection fails
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION - All countries, languages, domains, and translations
  // ============================================================================
  
  const COUNTRY_CONFIG = {
    // Countries with dedicated stores
    IT: {
      name: 'Italy',
      domain: 'miavola.it',
      language: 'it',
      translations: {
        it: {
          title: 'Miavola è disponibile nei seguenti paesi',
          message: 'Sembra che tu stia visitando il negozio tedesco. Vuoi passare al negozio italiano?',
          redirectButton: 'Vai al negozio italiano',
          stayButton: 'Rimani sul negozio tedesco'
        },
        en: {
          title: 'Miavola is available in the following countries',
          message: 'It looks like you\'re visiting the German store. Would you like to switch to the Italian store?',
          redirectButton: 'Go to Italian store',
          stayButton: 'Stay on German store'
        }
      }
    },
    FR: {
      name: 'France',
      domain: 'miavola.fr',
      language: 'fr',
      translations: {
        fr: {
          title: 'Miavola est disponible dans les pays suivants',
          message: 'Il semble que vous visitez le magasin allemand. Souhaitez-vous passer au magasin français?',
          redirectButton: 'Aller au magasin français',
          stayButton: 'Rester sur le magasin allemand'
        },
        en: {
          title: 'Miavola is available in the following countries',
          message: 'It looks like you\'re visiting the German store. Would you like to switch to the French store?',
          redirectButton: 'Go to French store',
          stayButton: 'Stay on German store'
        }
      }
    },
    // Additional countries for the grid (no redirect, just display)
    DE: { name: 'Germany', flag: '🇩🇪', domain: 'miavola.de' },
    AT: { name: 'Austria', flag: '🇦🇹', domain: null },
    BE: { name: 'Belgium', flag: '🇧🇪', domain: null },
    GR: { name: 'Greece', flag: '🇬🇷', domain: null },
    HR: { name: 'Croatia', flag: '🇭🇷', domain: null },
    CZ: { name: 'Czechia', flag: '🇨🇿', domain: null },
    DK: { name: 'Denmark', flag: '🇩🇰', domain: null },
    EE: { name: 'Estonia', flag: '🇪🇪', domain: null },
    FI: { name: 'Finland', flag: '🇫🇮', domain: null },
    HU: { name: 'Hungary', flag: '🇭🇺', domain: null },
    IE: { name: 'Ireland', flag: '🇮🇪', domain: null },
    LV: { name: 'Latvia', flag: '🇱🇻', domain: null },
    LT: { name: 'Lithuania', flag: '🇱🇹', domain: null },
    LU: { name: 'Luxembourg', flag: '🇱🇺', domain: null },
    NL: { name: 'Netherlands', flag: '🇳🇱', domain: null },
    NO: { name: 'Norway', flag: '🇳🇴', domain: null },
    PL: { name: 'Poland', flag: '🇵🇱', domain: null },
    RO: { name: 'Romania', flag: '🇷🇴', domain: null },
    SK: { name: 'Slovakia', flag: '🇸🇰', domain: null },
    SI: { name: 'Slovenia', flag: '🇸🇮', domain: null },
    ES: { name: 'Spain', flag: '🇪🇸', domain: null },
    SE: { name: 'Sweden', flag: '🇸🇪', domain: null },
    CH: { name: 'Switzerland', flag: '🇨🇭', domain: null },
    GB: { name: 'United Kingdom', flag: '🇬🇧', domain: null }
  };

  // Default English translations for countries without dedicated stores
  const DEFAULT_TRANSLATIONS = {
    en: {
      title: 'Miavola is available in the following countries',
      message: 'It looks like you\'re visiting the German store. Would you like to switch to your local store?',
      redirectButton: 'Go to my local store',
      stayButton: 'Stay on German store'
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Get user's choice from localStorage
   * @returns {string|null} 'redirect', 'stay', or null
   */
  function getUserChoice() {
    try {
      return localStorage.getItem('bd-geo-choice');
    } catch (e) {
      return null;
    }
  }

  /**
   * Save user's choice to localStorage
   * @param {string} choice - 'redirect' or 'stay'
   */
  function saveUserChoice(choice) {
    try {
      localStorage.setItem('bd-geo-choice', choice);
      // Also set expiry timestamp (30 days default)
      const expiry = Date.now() + (window.bdGeoDetectionConfig?.cookieExpiryDays || 30) * 24 * 60 * 60 * 1000;
      localStorage.setItem('bd-geo-choice-expiry', expiry.toString());
    } catch (e) {
      console.warn('Could not save user choice to localStorage:', e);
    }
  }

  /**
   * Check if user choice has expired
   * @returns {boolean}
   */
  function isUserChoiceExpired() {
    try {
      const expiry = localStorage.getItem('bd-geo-choice-expiry');
      if (!expiry) return true;
      return Date.now() > parseInt(expiry, 10);
    } catch (e) {
      return true;
    }
  }

  /**
   * Get translations for a country
   * @param {string} countryCode - ISO country code (e.g., 'IT', 'FR')
   * @returns {Object} Translation object
   */
  function getTranslations(countryCode) {
    const config = window.bdGeoDetectionConfig;
    if (!config) return DEFAULT_TRANSLATIONS.en;

    // First, check if country is configured in dynamic blocks
    if (config.countryBlocks && config.countryBlocks.length > 0) {
      const countryBlock = config.countryBlocks.find(block => 
        block.countryCode && block.countryCode.toUpperCase() === countryCode.toUpperCase()
      );

      if (countryBlock && countryBlock.title) {
        // Use translations from Shopify settings
        return {
          title: countryBlock.title || DEFAULT_TRANSLATIONS.en.title,
          message: countryBlock.message || DEFAULT_TRANSLATIONS.en.message,
          redirectButton: countryBlock.redirectButton || DEFAULT_TRANSLATIONS.en.redirectButton,
          stayButton: countryBlock.stayButton || DEFAULT_TRANSLATIONS.en.stayButton
        };
      }
    }

    // Fallback to hardcoded translations if no dynamic config found
    const country = COUNTRY_CONFIG[countryCode];
    if (!country) return DEFAULT_TRANSLATIONS.en;

    // If country has translations, use them
    if (country.translations) {
      // Prefer native language, fallback to English
      const lang = country.language || 'en';
      return country.translations[lang] || country.translations.en || DEFAULT_TRANSLATIONS.en;
    }

    // Default to English for countries without dedicated stores
    return DEFAULT_TRANSLATIONS.en;
  }

  /**
   * Detect country using multiple methods
   * Priority: 1. Shopify Liquid (request.country_code), 2. JS API fallback
   * @returns {Promise<string|null>} Country code or null if detection fails
   */
  async function detectCountry() {
    const config = window.bdGeoDetectionConfig;
    if (!config) return null;

    // Method 1: Use Shopify Liquid country code (most reliable)
    if (config.shopifyCountryCode && config.shopifyCountryCode.length === 2) {
      return config.shopifyCountryCode.toUpperCase();
    }

    // Method 2: Fallback to JavaScript API (CORS-friendly)
    // Try multiple APIs as fallback (ordered by reliability)
    const apiEndpoints = [
      config.geoApiUrl, // Custom API from settings (if provided)
      'https://get.geojs.io/v1/ip/country.json', // Simple, reliable CORS-friendly API
      'https://api.country.is', // Another CORS-friendly option
      'https://ip-api.com/json/?fields=status,countryCode' // May have rate limits
    ].filter(Boolean); // Remove empty values

    for (const apiUrl of apiEndpoints) {
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          continue; // Try next API
        }

        const data = await response.json();
        
        // Handle different API response formats
        let countryCode = null;
        
        // geojs.io format: { "country": "US" }
        if (data.country && typeof data.country === 'string' && data.country.length === 2) {
          countryCode = data.country;
        }
        // api.country.is format: { "country": "US", "ip": "..." }
        else if (data.country && typeof data.country === 'string' && data.country.length === 2) {
          countryCode = data.country;
        }
        // ip-api.com format: { "countryCode": "US" }
        else if (data.countryCode) {
          countryCode = data.countryCode;
        }
        // ipapi.co format: { "country_code": "US" }
        else if (data.country_code) {
          countryCode = data.country_code;
        }
        // Alternative format
        else if (data.country_code_iso2) {
          countryCode = data.country_code_iso2;
        }

        if (countryCode && countryCode.length === 2) {
          return countryCode.toUpperCase();
        }
      } catch (error) {
        // CORS error, network error, or API error (403, etc.) - try next API
        // Don't log here to avoid console spam - will log in test function if needed
        continue;
      }
    }

    // All APIs failed - silently return null (don't show popup if detection fails)
    console.warn('Geo-detection: All API methods failed. Popup will not be shown.');
    return null;
  }

  /**
   * Check if country should see the popup
   * @param {string} countryCode - ISO country code
   * @returns {boolean}
   */
  function shouldShowPopup(countryCode) {
    if (!countryCode) return false;

    const config = window.bdGeoDetectionConfig;
    if (!config || !config.enabled) return false;

    // Check if country is in allowed list (should NOT see popup)
    const allowedCountries = config.allowedCountries || ['DE'];
    if (allowedCountries.includes(countryCode)) {
      return false;
    }

    // Check if redirect domain is configured in dynamic settings FIRST
    const redirectDomain = config.redirectDomains?.[countryCode];
    if (redirectDomain) {
      // Domain is configured in settings - show popup
      return true;
    }

    // Fallback: Check hardcoded config (for backwards compatibility)
    const country = COUNTRY_CONFIG[countryCode];
    if (country && country.domain) {
      return true;
    }

    // No redirect domain configured for this country
    return false;
  }

  /**
   * Build country grid HTML
   * @param {string} detectedCountryCode - Highlighted country
   * @returns {string} HTML string
   */
  function buildCountryGrid(detectedCountryCode) {
    const config = window.bdGeoDetectionConfig;
    
    // Merge dynamic country blocks with static country data (flags, etc.)
    let countries = [];
    
    // First, add countries from dynamic blocks
    if (config.countryBlocks && config.countryBlocks.length > 0) {
      config.countryBlocks.forEach(block => {
        if (block.countryCode) {
          const code = block.countryCode.toUpperCase();
          const staticData = COUNTRY_CONFIG[code] || {};
          countries.push({
            code: code,
            name: block.countryName || staticData.name || code,
            domain: block.redirectDomain || staticData.domain || null
          });
        }
      });
    }
    
    // Add other countries from static config (for display in grid)
    Object.keys(COUNTRY_CONFIG).forEach(code => {
      // Only add if not already in the list
      if (!countries.find(c => c.code === code)) {
        countries.push({
          code: code,
          ...COUNTRY_CONFIG[code]
        });
      }
    });
    
    // Sort countries: detected country first, then alphabetically
    countries.sort((a, b) => {
      if (a.code === detectedCountryCode) return -1;
      if (b.code === detectedCountryCode) return 1;
      return a.name.localeCompare(b.name);
    });

    return countries
      .map(country => {
        const isDetected = country.code === detectedCountryCode;
        const hasDomain = country.domain !== null && country.domain !== undefined;
        const config = window.bdGeoDetectionConfig;
        const hasRedirectDomain = config?.redirectDomains?.[country.code];
        
        // Convert country code to lowercase for flag-icons (e.g., GB -> gb)
        const flagCode = country.code.toLowerCase();
        
        return `
          <div class="bd-geo-detection-popup__country-item ${isDetected ? 'bd-geo-detection-popup__country-item--detected' : ''} ${hasDomain || hasRedirectDomain ? 'bd-geo-detection-popup__country-item--has-domain' : ''}" 
               data-country-code="${country.code}">
            <div class="bd-geo-detection-popup__country-flag">
              <span class="fi fi-${flagCode}"></span>
            </div>
            <div class="bd-geo-detection-popup__country-name">${country.name}</div>
          </div>
        `;
      })
      .join('');
  }

  /**
   * Populate popup with translations and country grid
   * @param {string} countryCode - Detected country code
   */
  function populatePopup(countryCode) {
    const popup = document.getElementById('bd-geo-detection-popup');
    if (!popup) return;

    const translations = getTranslations(countryCode);
    const country = COUNTRY_CONFIG[countryCode];

    // Update title
    const titleEl = popup.querySelector('[data-popup-title]');
    if (titleEl) {
      titleEl.textContent = translations.title;
    }

    // Update message
    const messageEl = popup.querySelector('[data-popup-message]');
    if (messageEl) {
      messageEl.textContent = translations.message;
    }

    // Update buttons
    const redirectBtn = popup.querySelector('[data-redirect-button]');
    if (redirectBtn) {
      redirectBtn.textContent = translations.redirectButton;
    }

    const stayBtn = popup.querySelector('[data-stay-button]');
    if (stayBtn) {
      stayBtn.textContent = translations.stayButton;
    }

    // Country grid: use existing Liquid-rendered items if present, else build from config
    const gridEl = popup.querySelector('[data-country-grid]');
    if (gridEl) {
      const existingItems = gridEl.querySelectorAll('.bd-geo-detection-popup__country-item');
      if (existingItems.length > 0) {
        // Grid was rendered by Liquid (localization.available_countries) – only update detected state
        existingItems.forEach(item => {
          const code = item.getAttribute('data-country-code');
          item.classList.toggle('bd-geo-detection-popup__country-item--detected', code === countryCode);
        });
      } else {
        // Fallback: build grid from JS config (e.g. when Liquid didn't render)
        gridEl.innerHTML = buildCountryGrid(countryCode);
      }
    }
  }

  /**
   * Show the popup
   */
  function showPopup() {
    const popup = document.getElementById('bd-geo-detection-popup');
    if (!popup) return;

    popup.style.display = 'block';
    popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  }

  /**
   * Hide the popup
   */
  function hidePopup() {
    const popup = document.getElementById('bd-geo-detection-popup');
    if (!popup) return;

    popup.style.display = 'none';
    popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = ''; // Restore scrolling
  }

  /**
   * Redirect to country-specific domain
   * @param {string} countryCode - Country code to redirect to
   */
  function redirectToCountry(countryCode) {
    const config = window.bdGeoDetectionConfig;
    if (!config) return;

    // Get redirect domain from dynamic settings first
    let redirectDomain = config.redirectDomains?.[countryCode];
    
    // Fallback to hardcoded config if not in dynamic settings
    if (!redirectDomain) {
      const country = COUNTRY_CONFIG[countryCode];
      redirectDomain = country?.domain;
    }
    
    if (!redirectDomain) {
      console.warn('No redirect domain configured for country:', countryCode);
      return;
    }
    
    // Preserve current path and query parameters
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const protocol = window.location.protocol;
    
    // Build redirect URL
    const redirectUrl = `${protocol}//${redirectDomain}${currentPath}${currentSearch}`;
    
    // Perform redirect
    window.location.href = redirectUrl;
  }

  /**
   * Initialize event listeners
   */
  function initEventListeners() {
    const popup = document.getElementById('bd-geo-detection-popup');
    if (!popup) return;

    // Close button and overlay
    const closeElements = popup.querySelectorAll('[data-close-popup]');
    closeElements.forEach(el => {
      el.addEventListener('click', () => {
        saveUserChoice('stay');
        hidePopup();
      });
    });

    // Stay button
    const stayBtn = popup.querySelector('[data-stay-button]');
    if (stayBtn) {
      stayBtn.addEventListener('click', () => {
        saveUserChoice('stay');
        hidePopup();
      });
    }

    // Redirect button
    const redirectBtn = popup.querySelector('[data-redirect-button]');
    if (redirectBtn) {
      redirectBtn.addEventListener('click', () => {
        const countryCode = redirectBtn.dataset.targetCountry;
        if (countryCode) {
          saveUserChoice('redirect');
          redirectToCountry(countryCode);
        }
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.style.display !== 'none') {
        saveUserChoice('stay');
        hidePopup();
      }
    });
  }

  /**
   * Main initialization function
   */
  async function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    const config = window.bdGeoDetectionConfig;
    if (!config || !config.enabled) return;

    // Check if user has already made a choice
    const userChoice = getUserChoice();
    if (userChoice && !isUserChoiceExpired()) {
      // User already made a choice, don't show popup
      return;
    }

    // Detect country
    const countryCode = await detectCountry();
    
    // Debug logging (only in development - remove in production if needed)
    if (window.location.hostname.includes('miavola.de') && !countryCode) {
      console.warn('Geo-detection: Country not detected. Popup will not be shown.');
    }
    
    // Check if we should show the popup
    if (!shouldShowPopup(countryCode)) {
      // Debug: Log why popup is not showing
      if (countryCode && window.location.hostname.includes('miavola.de')) {
        const redirectDomain = config.redirectDomains?.[countryCode];
        const allowedCountries = config.allowedCountries || ['DE'];
        console.log('Geo-detection debug:', {
          countryCode: countryCode,
          hasRedirectDomain: !!redirectDomain,
          redirectDomain: redirectDomain,
          isAllowed: allowedCountries.includes(countryCode),
          enabled: config.enabled
        });
      }
      return;
    }

    // Populate and show popup
    populatePopup(countryCode);
    
    // Set target country on redirect button
    const redirectBtn = document.querySelector('[data-redirect-button]');
    if (redirectBtn) {
      redirectBtn.dataset.targetCountry = countryCode;
    }

    // Initialize event listeners
    initEventListeners();

    // Show popup after a short delay for better UX
    setTimeout(() => {
      showPopup();
    }, 500);
  }

  // Start initialization
  init();

  // Debug: show popup from console (e.g. bdGeoShowPopup('IT') or bdGeoShowPopup())
  window.bdGeoShowPopup = function(countryCode) {
    const popup = document.getElementById('bd-geo-detection-popup');
    if (!popup) {
      console.warn('BD Geo: popup element not found.');
      return;
    }
    const code = (countryCode && String(countryCode).length === 2)
      ? String(countryCode).toUpperCase()
      : 'IT';
    populatePopup(code);
    const redirectBtn = document.querySelector('[data-redirect-button]');
    if (redirectBtn) redirectBtn.dataset.targetCountry = code;
    initEventListeners();
    showPopup();
  };

})();

